/**
 * Listing focus channel for Search Results Map Variant.
 *
 * Provides a single, stable publish/subscribe channel that decouples the
 * list-view hover source (standardCard.js) from the map plugin that reacts
 * to it (listingHighlight.plugin).
 *
 * Design rationale:
 * - A BehaviorSubject/Subject is kept private so callers can only push
 *   values through `highlightListing`, not `.complete()` or `.error()` them.
 * - `listing$` exposes only the read-only Observable projection so plugins
 *   can subscribe without being able to emit.
 * - `distinctUntilChanged` lives here, at the source, so every downstream
 *   subscriber gets deduplication for free.
 *
 * @module searchResultsMapVariant/services/listingFocus
 */

import { Subject, distinctUntilChanged } from 'rxjs';

const focusedListing$ = new Subject();

/**
 * Observable stream of focused listing changes.
 * Emits `{ id, communityId }` when a listing is highlighted, or `null` when
 * the highlight is cleared. Deduplicated by identity — consecutive identical
 * values are suppressed.
 *
 * @type {Observable<{ id: string, communityId: string } | null>}
 */
export const listing$ = focusedListing$.pipe(
  distinctUntilChanged((a, b) => a?.id === b?.id && a?.communityId === b?.communityId),
);

/**
 * Signals that a marker should be highlighted on the map.
 *
 * Call with a marker object to highlight, or with no argument (or null) to
 * clear any active highlight.
 *
 * Callers do not need to know anything about the map feature — they just
 * describe intent ("highlight this listing"), and the map reacts.
 *
 * @param {{ id: string, communityId: string } | null} [marker]
 */
export function markerHighlight(marker) {
  focusedListing$.next(marker ?? null);
}
